import React, { useContext, useEffect, useMemo, useState } from "react";
import {
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControlLabel,
  IconButton,
  InputAdornment,
  makeStyles,
  MenuItem,
  Paper,
  Slider,
  TextField,
  Tooltip,
  Typography
} from "@material-ui/core";
import {
  Add,
  DeleteOutline,
  Edit,
  Launch,
  Refresh,
  Star,
  StarBorder
} from "@material-ui/icons";
import { toast } from "react-toastify";

import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import ConfirmationModal from "../../components/ConfirmationModal";
import Title from "../../components/Title";
import { AuthContext } from "../../context/Auth/AuthContext";
import toastError from "../../errors/toastError";
import api from "../../services/api";
import {
  openSidepanelApp,
  syncSidepanelAppsWithExtension
} from "../../utils/sidepanelApps";

const emptyForm = {
  id: "",
  name: "",
  url: "",
  scope: "company",
  isDefault: false,
  zoomLevel: 100
};

const normalizeExternalUrl = value => {
  const trimmedValue = String(value || "").trim();
  if (!trimmedValue) return "";

  if (
    /^https?:\/\//i.test(trimmedValue) ||
    /^mailto:/i.test(trimmedValue) ||
    /^tel:/i.test(trimmedValue) ||
    trimmedValue.startsWith("/")
  ) {
    return trimmedValue;
  }

  return `https://${trimmedValue}`;
};

const useStyles = makeStyles(theme => ({
  mainPaper: {
    flex: 1,
    margin: 20,
    overflowY: "auto",
    padding: theme.spacing(2),
    ...theme.scrollbarStyles,
    [theme.breakpoints.down("xs")]: {
      margin: theme.spacing(1),
      padding: theme.spacing(1.5)
    }
  },
  layout: {
    display: "grid",
    gap: theme.spacing(2),
    gridTemplateColumns: "minmax(300px, 380px) minmax(0, 1fr)",
    [theme.breakpoints.down("sm")]: {
      gridTemplateColumns: "1fr"
    }
  },
  panel: {
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 8,
    padding: theme.spacing(2)
  },
  form: {
    display: "grid",
    gap: theme.spacing(2)
  },
  zoomRow: {
    alignItems: "center",
    display: "grid",
    gap: theme.spacing(1.5),
    gridTemplateColumns: "1fr 52px"
  },
  actions: {
    display: "flex",
    gap: theme.spacing(1),
    justifyContent: "flex-end",
    [theme.breakpoints.down("xs")]: {
      flexDirection: "column"
    }
  },
  appList: {
    display: "grid",
    gap: theme.spacing(1)
  },
  appSection: {
    "& + &": {
      marginTop: theme.spacing(2)
    }
  },
  sectionHeader: {
    alignItems: "baseline",
    display: "flex",
    gap: theme.spacing(1),
    justifyContent: "space-between",
    marginBottom: theme.spacing(1)
  },
  appCard: {
    alignItems: "center",
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 8,
    display: "grid",
    gap: theme.spacing(1),
    gridTemplateColumns: "1fr auto",
    padding: theme.spacing(1.5),
    [theme.breakpoints.down("xs")]: {
      gridTemplateColumns: "1fr"
    }
  },
  appTitle: {
    alignItems: "center",
    display: "flex",
    gap: theme.spacing(1),
    minWidth: 0
  },
  appUrl: {
    wordBreak: "break-all"
  },
  appActions: {
    display: "flex",
    gap: theme.spacing(0.5),
    justifyContent: "flex-end"
  },
  emptyState: {
    border: `1px dashed ${theme.palette.divider}`,
    borderRadius: 8,
    color: theme.palette.text.secondary,
    padding: theme.spacing(4),
    textAlign: "center"
  },
  helper: {
    color: theme.palette.text.secondary,
    marginTop: theme.spacing(1)
  },
  loadingState: {
    alignItems: "center",
    color: theme.palette.text.secondary,
    display: "flex",
    gap: theme.spacing(1),
    justifyContent: "center",
    minHeight: 160
  }
}));

function normalizeApps(apps, fallbackScope = "company") {
  let defaultFound = false;

  return apps
    .filter(app => app?.url)
    .map(app => {
      const isDefault = Boolean(app.isDefault) && !defaultFound;
      if (isDefault) defaultFound = true;

      return {
        id: String(app.id || Date.now()),
        name: String(app.name || "App sin nombre").trim(),
        url: String(app.url || "").trim(),
        scope:
          app.scope === "global" || app.isGlobal || fallbackScope === "global"
            ? "global"
            : "company",
        isDefault,
        zoomLevel: Number(app.zoomLevel || 100),
        createdAt: app.createdAt || new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };
    });
}

function normalizeApp(app, fallbackScope = "company") {
  return normalizeApps([app], fallbackScope)[0] || null;
}

function splitAppsByScope(apps) {
  return {
    globalApps: apps.filter(app => app.scope === "global"),
    companyApps: apps.filter(app => app.scope !== "global")
  };
}

function upsertApp(apps, nextApp) {
  const exists = apps.some(app => String(app.id) === String(nextApp.id));
  return exists
    ? apps.map(app => (String(app.id) === String(nextApp.id) ? nextApp : app))
    : [...apps, nextApp];
}

function removeAppById(apps, appId) {
  return apps.filter(app => String(app.id) !== String(appId));
}

function applySingleDefault(apps, selectedApp) {
  return apps.map(app => ({
    ...app,
    isDefault: String(app.id) === String(selectedApp.id)
  }));
}

function buildValidatableUrl(value) {
  return String(value || "")
    .trim()
    .replace(/\{[^{}]+\}/g, "template-value");
}

function isValidTemplateUrl(value) {
  try {
    const url = new URL(buildValidatableUrl(value));
    return ["http:", "https:"].includes(url.protocol);
  } catch {
    return false;
  }
}

const SidepanelApps = () => {
  const classes = useStyles();
  const { user } = useContext(AuthContext);
  const [globalApps, setGlobalApps] = useState([]);
  const [companyApps, setCompanyApps] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [deletingApp, setDeletingApp] = useState(null);
  const [extensionInstallUrl, setExtensionInstallUrl] = useState("");
  const [extensionModalOpen, setExtensionModalOpen] = useState(false);

  const isSuperAdmin = user?.profile === "superadmin";
  const editing = Boolean(form.id);
  const apps = useMemo(
    () => [...globalApps, ...companyApps],
    [companyApps, globalApps]
  );
  const defaultApp = useMemo(
    () => apps.find(app => app.isDefault) || apps[0] || null,
    [apps]
  );

  const loadApps = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/apps");
      setGlobalApps(normalizeApps(data.globalApps || [], "global"));
      setCompanyApps(normalizeApps(data.companyApps || [], "company"));
      window.dispatchEvent(new CustomEvent("sidepanel-apps-updated"));
      syncAppsWithExtension(data.applications || []).catch(() => undefined);
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  };

  const syncLocalApps = nextApps => {
    window.dispatchEvent(new CustomEvent("sidepanel-apps-updated"));
    syncAppsWithExtension(nextApps).catch(() => undefined);
  };

  useEffect(() => {
    loadApps();
  }, []);

  useEffect(() => {
    let mounted = true;

    const fetchExtensionInstallUrl = async () => {
      try {
        const { data } = await api.get(
          "/settings/sidepanel-extension-install-url"
        );
        if (mounted) {
          setExtensionInstallUrl(normalizeExternalUrl(data?.value));
        }
      } catch {
        if (mounted) setExtensionInstallUrl("");
      }
    };

    const handleExtensionInstallUrlChanged = event => {
      setExtensionInstallUrl(normalizeExternalUrl(event.detail?.value));
    };

    fetchExtensionInstallUrl();
    window.addEventListener(
      "sidepanelExtensionInstallUrlChanged",
      handleExtensionInstallUrlChanged
    );

    return () => {
      mounted = false;
      window.removeEventListener(
        "sidepanelExtensionInstallUrlChanged",
        handleExtensionInstallUrlChanged
      );
    };
  }, []);

  const syncAppsWithExtension = async nextApps => {
    setSyncing(true);
    try {
      const response = await syncSidepanelAppsWithExtension(nextApps);

      if (!response?.success) {
        throw new Error(response?.error || "La extension no pudo guardar las apps.");
      }

      return true;
    } catch (error) {
      setExtensionModalOpen(true);
      return false;
    } finally {
      setSyncing(false);
    }
  };

  const handleChange = event => {
    const { name, value, checked, type } = event.target;
    setForm(prevState => ({
      ...prevState,
      [name]: type === "checkbox" ? checked : value
    }));
  };

  const resetForm = () =>
    setForm({
      ...emptyForm,
      scope: isSuperAdmin ? "global" : "company"
    });

  useEffect(() => {
    resetForm();
  }, [isSuperAdmin]);

  const validateForm = () => {
    if (!form.name.trim()) {
      toast.warn("Ingresa un nombre para la app.");
      return false;
    }

    if (!form.url.trim()) {
      toast.warn("Ingresa la URL de la app.");
      return false;
    }

    if (!isValidTemplateUrl(form.url)) {
      toast.warn("Ingresa una URL valida.");
      return false;
    }

    return true;
  };

  const handleSubmit = async event => {
    event.preventDefault();
    if (!validateForm()) return;

    setSaving(true);
    const appData = {
      ...form,
      name: form.name.trim(),
      url: form.url.trim(),
      scope: form.scope === "global" && isSuperAdmin ? "global" : "company",
      zoomLevel: Number(form.zoomLevel || 100)
    };

    try {
      const { data } = form.id
        ? await api.put(`/apps/${form.id}`, appData)
        : await api.post("/apps", appData);
      const savedApp = normalizeApp(data, appData.scope);
      if (!savedApp) return;

      const currentApps = [...globalApps, ...companyApps];
      const withoutSavedApp = removeAppById(currentApps, savedApp.id);
      const nextApps = upsertApp(
        savedApp.isDefault
          ? withoutSavedApp.map(app => ({ ...app, isDefault: false }))
          : withoutSavedApp,
        savedApp
      );
      const nextSplitApps = splitAppsByScope(nextApps);

      setGlobalApps(nextSplitApps.globalApps);
      setCompanyApps(nextSplitApps.companyApps);
      syncLocalApps(nextApps);

      toast.success("App guardada.");
      resetForm();
    } catch (err) {
      toastError(err);
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = app => {
    setForm({
      id: app.id,
      name: app.name,
      url: app.url,
      scope: app.scope === "global" ? "global" : "company",
      isDefault: Boolean(app.isDefault),
      zoomLevel: Number(app.zoomLevel || 100)
    });
  };

  const handleDelete = async app => {
    if (!app) return;

    try {
      await api.delete(`/apps/${app.id}`);
      const nextGlobalApps = removeAppById(globalApps, app.id);
      const nextCompanyApps = removeAppById(companyApps, app.id);
      const nextApps = [...nextGlobalApps, ...nextCompanyApps];

      setGlobalApps(nextGlobalApps);
      setCompanyApps(nextCompanyApps);
      syncLocalApps(nextApps);

      if (String(form.id) === String(app.id)) resetForm();
      toast.success("App eliminada.");
    } catch (err) {
      toastError(err);
    } finally {
      setDeletingApp(null);
    }
  };

  const handleSetDefault = async app => {
    const previousGlobalApps = globalApps;
    const previousCompanyApps = companyApps;
    const nextApps = applySingleDefault([...globalApps, ...companyApps], app);
    const nextSplitApps = splitAppsByScope(nextApps);

    setGlobalApps(nextSplitApps.globalApps);
    setCompanyApps(nextSplitApps.companyApps);

    try {
      await api.put(`/apps/${app.id}`, { isDefault: true });
      syncLocalApps(nextApps);
      toast.success("App marcada como predeterminada.");
    } catch (err) {
      setGlobalApps(previousGlobalApps);
      setCompanyApps(previousCompanyApps);
      toastError(err);
    }
  };

  const handleOpenApp = async app => {
    try {
      const response = await openSidepanelApp(app);

      if (!response?.success) {
        throw new Error(response?.error || "No se pudo abrir el sidepanel.");
      }

      toast.success(
        response.opened
          ? "App abierta en el sidepanel."
          : "Sidepanel preparado. Abre el panel desde el icono de la extension."
      );
    } catch (error) {
      setExtensionModalOpen(true);
    }
  };

  const handleReloadDefault = async () => {
    if (!defaultApp) {
      toast.warn("No hay apps configuradas.");
      return;
    }

    await handleOpenApp(defaultApp);
  };

  const renderAppList = (items, title, description, options = {}) => (
    <div className={classes.appSection}>
      <div className={classes.sectionHeader}>
        <div>
          <Typography variant="subtitle1">{title}</Typography>
          {description && (
            <Typography variant="caption" color="textSecondary">
              {description}
            </Typography>
          )}
        </div>
        <Typography variant="caption" color="textSecondary">
          {items.length} apps
        </Typography>
      </div>

      {items.length ? (
        <div className={classes.appList}>
          {items.map(app => (
            <div className={classes.appCard} key={`${app.scope}-${app.id}`}>
              <div>
                <div className={classes.appTitle}>
                  {app.isDefault ? (
                    <Star color="primary" fontSize="small" />
                  ) : (
                    <StarBorder color="disabled" fontSize="small" />
                  )}
                  <Typography variant="subtitle1">{app.name}</Typography>
                  <Typography variant="caption" color="textSecondary">
                    {app.zoomLevel || 100}%
                  </Typography>
                </div>
                {!options.hideUrl && (
                  <Typography
                    variant="body2"
                    color="textSecondary"
                    className={classes.appUrl}
                  >
                    {app.url}
                  </Typography>
                )}
              </div>

              <div className={classes.appActions}>
                <Tooltip title="Abrir en sidepanel">
                  <IconButton size="small" onClick={() => handleOpenApp(app)}>
                    <Launch fontSize="small" />
                  </IconButton>
                </Tooltip>
                {!app.isDefault && (
                  <Tooltip title="Marcar como predeterminada">
                    <IconButton size="small" onClick={() => handleSetDefault(app)}>
                      <StarBorder fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
                {!options.readOnly && (app.scope !== "global" || isSuperAdmin) && (
                  <>
                    <Tooltip title="Editar">
                      <IconButton size="small" onClick={() => handleEdit(app)}>
                        <Edit fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Eliminar">
                      <IconButton size="small" onClick={() => setDeletingApp(app)}>
                        <DeleteOutline fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </>
                )}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className={classes.emptyState}>
          <Typography variant="body2">No hay apps en esta seccion.</Typography>
        </div>
      )}
    </div>
  );

  return (
    <MainContainer>
      <MainHeader>
        <Title>Apps</Title>
        <MainHeaderButtonsWrapper>
          <Button
            variant="outlined"
            color="primary"
            startIcon={<Refresh />}
            onClick={handleReloadDefault}
            disabled={!apps.length}
          >
            Abrir predeterminada
          </Button>
          <Button
            variant="contained"
            color="primary"
            startIcon={<Add />}
            onClick={resetForm}
          >
            Nueva app
          </Button>
        </MainHeaderButtonsWrapper>
      </MainHeader>

      <Paper className={classes.mainPaper} variant="outlined">
        <div className={classes.layout}>
          <section className={classes.panel}>
            <Typography variant="h6" gutterBottom>
              {editing ? "Editar app" : "Crear app"}
            </Typography>
            {isSuperAdmin && (
              <Typography variant="body2" color="textSecondary" gutterBottom>
                Como superadmin puedes guardar apps globales visibles para todas las cuentas.
              </Typography>
            )}

            <form className={classes.form} onSubmit={handleSubmit}>
              <TextField
                label="Nombre"
                name="name"
                value={form.name}
                onChange={handleChange}
                variant="outlined"
                size="small"
                fullWidth
              />

              <TextField
                label="URL"
                name="url"
                value={form.url}
                onChange={handleChange}
                variant="outlined"
                size="small"
                fullWidth
                placeholder="https://mi-app.com?telefono={phoneNumber}"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Launch fontSize="small" />
                    </InputAdornment>
                  )
                }}
              />

              {isSuperAdmin && (
                <TextField
                  select
                  label="Visibilidad"
                  name="scope"
                  value={form.scope}
                  onChange={handleChange}
                  variant="outlined"
                  size="small"
                  fullWidth
                >
                  <MenuItem value="global">Global para todas las cuentas</MenuItem>
                  <MenuItem value="company">Solo esta cuenta</MenuItem>
                </TextField>
              )}

              <div>
                <Typography variant="body2" color="textSecondary" gutterBottom>
                  Zoom del panel
                </Typography>
                <div className={classes.zoomRow}>
                  <Slider
                    value={Number(form.zoomLevel || 100)}
                    min={50}
                    max={130}
                    step={5}
                    onChange={(event, value) =>
                      setForm(prevState => ({ ...prevState, zoomLevel: value }))
                    }
                  />
                  <Typography variant="body2">{form.zoomLevel}%</Typography>
                </div>
              </div>

              <FormControlLabel
                control={
                  <Checkbox
                    checked={Boolean(form.isDefault)}
                    onChange={handleChange}
                    name="isDefault"
                    color="primary"
                  />
                }
                label="Marcar como app predeterminada"
              />

              <div className={classes.actions}>
                {editing && (
                  <Button onClick={resetForm} disabled={saving}>
                    Cancelar
                  </Button>
                )}
                <Button
                  type="submit"
                  color="primary"
                  variant="contained"
                  disabled={saving || syncing}
                >
                  Guardar
                </Button>
              </div>
            </form>

            <Typography variant="caption" className={classes.helper} component="p">
              Variables disponibles: {"{ticketId}"}, {"{contactId}"}, {"{name}"}, {"{phoneNumber}"} y campos anidados como {"{contact.email}"}.
            </Typography>
          </section>

          <section className={classes.panel}>
            <Typography variant="h6" gutterBottom>
              Apps configuradas
            </Typography>

            {loading ? (
              <div className={classes.loadingState}>
                <CircularProgress size={22} />
                <span>Cargando apps...</span>
              </div>
            ) : apps.length ? (
              <>
                {renderAppList(
                  globalApps,
                  "Globales",
                  "Disponibles para todas las cuentas.",
                  { hideUrl: !isSuperAdmin, readOnly: !isSuperAdmin }
                )}
                {renderAppList(
                  companyApps,
                  "De esta cuenta",
                  "Disponibles solo para los usuarios de esta cuenta."
                )}
              </>
            ) : (
              <div className={classes.emptyState}>
                <Typography variant="subtitle1">No hay apps configuradas.</Typography>
                <Typography variant="body2">
                  Crea una app para abrirla en el sidepanel de Chrome.
                </Typography>
              </div>
            )}
          </section>
        </div>
      </Paper>
      <Dialog
        open={extensionModalOpen}
        onClose={() => setExtensionModalOpen(false)}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>Instalar extension de sidepanel</DialogTitle>
        <DialogContent dividers>
          <DialogContentText>
            Las apps se guardaron en el CRM, pero no se detecto la extension de
            sidepanel en este navegador. Instalala o recargala para sincronizar
            y abrir apps en el panel lateral.
          </DialogContentText>
          {extensionInstallUrl ? (
            <Typography variant="body2">
              Link:{" "}
              <a
                href={extensionInstallUrl}
                target="_blank"
                rel="noopener noreferrer"
              >
                {extensionInstallUrl}
              </a>
            </Typography>
          ) : (
            <Typography variant="body2" color="textSecondary">
              El superadministrador aun no configuro el link de instalacion.
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setExtensionModalOpen(false)}
            color="secondary"
          >
            Cerrar
          </Button>
          {extensionInstallUrl && (
            <Button
              color="primary"
              variant="contained"
              component="a"
              href={extensionInstallUrl}
              target="_blank"
              rel="noopener noreferrer"
            >
              Instalar extension
            </Button>
          )}
        </DialogActions>
      </Dialog>
      <ConfirmationModal
        open={Boolean(deletingApp)}
        title={`Eliminar app ${deletingApp?.name || ""}?`}
        onClose={() => setDeletingApp(null)}
        onConfirm={() => handleDelete(deletingApp)}
      >
        Esta accion no se puede deshacer.
      </ConfirmationModal>
    </MainContainer>
  );
};

export default SidepanelApps;
